import { FormEvent, useState } from 'react'
import { Mail, MapPin, Phone, Send } from 'lucide-react'
import PageHero from '../components/PageHero'
import RecaptchaCheckbox from '../components/RecaptchaCheckbox'
import Reveal from '../components/Reveal'
import { contact } from '../data/site'

const reasons = ['Donation or sponsorship', 'Volunteer availability', 'Visit request', 'Media or community partnership', 'General question'] as const
type FormValues = { name: string; email: string; phone: string; reason: string; message: string; website: string }
type FieldName = keyof FormValues | 'recaptchaToken'
type Errors = Partial<Record<FieldName, string>>

type SharedApiResponse = {
  message?: string
  error?: string | { code?: string; message?: string }
  fieldErrors?: Record<string, string>
}

const initialValues: FormValues = { name: '', email: '', phone: '', reason: '', message: '', website: '' }
const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const phonePattern = /^[+()\-.\s\d]{7,25}$/

const validate = (values: FormValues, recaptchaToken: string): Errors => {
  const errors: Errors = {}
  const name = values.name.trim()
  const email = values.email.trim()
  const phone = values.phone.trim()
  const message = values.message.trim()
  if (name.length < 2) errors.name = 'Enter at least 2 characters.'
  else if (name.length > 100) errors.name = 'Use 100 characters or fewer.'
  if (!emailPattern.test(email)) errors.email = 'Enter a valid email address.'
  else if (email.length > 254) errors.email = 'Use 254 characters or fewer.'
  if (phone && !phonePattern.test(phone)) errors.phone = 'Enter a valid phone number using up to 25 characters.'
  if (!reasons.includes(values.reason as (typeof reasons)[number])) errors.reason = 'Select a reason from the list.'
  if (message.length < 10) errors.message = 'Enter at least 10 characters.'
  else if (message.length > 2500) errors.message = 'Use 2500 characters or fewer.'
  if (!recaptchaToken) errors.recaptchaToken = 'Please complete the security verification and try again.'
  return errors
}

export default function ContactPage() {
  const [values, setValues] = useState<FormValues>(initialValues)
  const [errors, setErrors] = useState<Errors>({})
  const [recaptchaToken, setRecaptchaToken] = useState('')
  const [resetSignal, setResetSignal] = useState(0)
  const [status, setStatus] = useState('')
  const [sending, setSending] = useState(false)
  const apiBase = import.meta.env.VITE_FORMS_API_BASE_URL?.trim().replace(/\/+$/, '') || ''
  const siteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY?.trim() || ''

  const update = (field: keyof FormValues, value: string) => {
    setValues((current) => ({ ...current, [field]: value }))
    setErrors((current) => ({ ...current, [field]: undefined }))
  }

  const focusFirstError = (nextErrors: Errors) => {
    const first = (['name', 'email', 'phone', 'reason', 'message', 'recaptchaToken'] as FieldName[]).find((field) => nextErrors[field])
    window.setTimeout(() => document.getElementById(first === 'recaptchaToken' ? 'recaptcha-group' : `contact-${first}`)?.focus(), 0)
  }

  const resetRecaptcha = () => {
    setRecaptchaToken('')
    setResetSignal((value) => value + 1)
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (sending) return
    const nextErrors = validate(values, recaptchaToken)
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors)
      setStatus('Please correct the highlighted fields.')
      focusFirstError(nextErrors)
      return
    }
    if (!apiBase) {
      setStatus('Secure form delivery is not configured yet. Please call or email the sanctuary directly.')
      return
    }

    setSending(true)
    setStatus('Sending...')
    try {
      const response = await fetch(`${apiBase}/api/send-contact`, {
        method: 'POST',
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          site: 'unique-diamond-horse-sanctuary',
          websiteName: 'Unique Diamond Horse Sanctuary',
          formType: 'contact',
          name: values.name.trim(),
          email: values.email.trim(),
          phone: values.phone.trim(),
          reason: values.reason,
          message: values.message.trim(),
          website: values.website,
          pageUrl: window.location.href,
          submittedAt: new Date().toISOString(),
          recaptchaToken,
        }),
        signal: AbortSignal.timeout(20000),
      })

      const result = await response.json().catch(() => ({})) as SharedApiResponse

      if (!response.ok) {
        if (response.status === 429) {
          throw new Error('Too many requests have been sent. Please wait a few minutes and try again.')
        }

        const backendErrors: Errors = {}
        for (const [field, message] of Object.entries(result.fieldErrors || {})) {
          if (typeof message === 'string') backendErrors[field as FieldName] = message
        }

        if (Object.keys(backendErrors).length) {
          setErrors(backendErrors)
          focusFirstError(backendErrors)
        }

        const errorCode = typeof result.error === 'object' ? result.error?.code : undefined
        const errorMessage = typeof result.error === 'object' ? result.error?.message : result.error

        if (errorCode === 'verification_failed') {
          resetRecaptcha()
          setErrors((current) => ({
            ...current,
            recaptchaToken: 'Please complete the security verification and try again.',
          }))
        }

        throw new Error(errorMessage || result.message || 'The form could not be sent. Please try again.')
      }

      setValues(initialValues)
      setErrors({})
      setStatus(result.message || 'Thank you. Your request has been sent successfully.')
      resetRecaptcha()
    } catch (error) {
      setStatus(error instanceof Error ? error.message : 'The form could not be sent. Please try again.')
      if (recaptchaToken) resetRecaptcha()
    } finally {
      setSending(false)
    }
  }

  const fieldError = (field: FieldName) => errors[field] ? <span className="field-error" id={`${field}-error`}>{errors[field]}</span> : null

  return (
    <>
      <PageHero eyebrow="Contact the sanctuary" title="Reach out with a question, support offer, or visit request." image="/images/horse-leonardo.webp">
        <p>Please remember that the sanctuary is at full capacity and visits are limited to requests reviewed in advance.</p>
      </PageHero>
      <section className="section section--cream">
        <div className="container contact-layout">
          <Reveal className="contact-details">
            <span className="eyebrow">Direct contact</span><h2>We would be glad to hear from you.</h2>
            <p>For the fastest response, include the reason for your message and any relevant dates or details.</p>
            <a href={`https://maps.google.com/?q=${encodeURIComponent(contact.address)}`} target="_blank" rel="noopener noreferrer"><MapPin /><span><strong>Mailing address</strong>{contact.address}</span></a>
            <a href={`tel:${contact.phone.replace(/\D/g, '')}`}><Phone /><span><strong>Phone</strong>{contact.phone}</span></a>
            {contact.emails.map((email) => <a key={email} href={`mailto:${email}`}><Mail /><span><strong>Email</strong>{email}</span></a>)}
            <div className="contact-details__note"><strong>Visits are by approved request only.</strong><span>Please do not arrive at the sanctuary without confirmation.</span></div>
          </Reveal>

          <Reveal className="contact-form-card" delay={100}>
            <span className="eyebrow">Send a secure request</span><h2>How can we help?</h2>
            <form onSubmit={handleSubmit} noValidate>
              <label htmlFor="contact-name">Full name
                <input id="contact-name" name="name" autoComplete="name" minLength={2} maxLength={100} required value={values.name} onChange={(event) => update('name', event.target.value)} aria-invalid={Boolean(errors.name)} aria-describedby={errors.name ? 'name-error' : undefined} />{fieldError('name')}
              </label>
              <div className="form-row">
                <label htmlFor="contact-email">Email<input id="contact-email" name="email" type="email" autoComplete="email" maxLength={254} required value={values.email} onChange={(event) => update('email', event.target.value)} aria-invalid={Boolean(errors.email)} aria-describedby={errors.email ? 'email-error' : undefined} />{fieldError('email')}</label>
                <label htmlFor="contact-phone">Phone <span className="optional-label">Optional</span><input id="contact-phone" name="phone" type="tel" autoComplete="tel" maxLength={25} value={values.phone} onChange={(event) => update('phone', event.target.value)} aria-invalid={Boolean(errors.phone)} aria-describedby={errors.phone ? 'phone-error' : undefined} />{fieldError('phone')}</label>
              </div>
              <label htmlFor="contact-reason">Reason for contacting us
                <select id="contact-reason" name="reason" required value={values.reason} onChange={(event) => update('reason', event.target.value)} aria-invalid={Boolean(errors.reason)} aria-describedby={errors.reason ? 'reason-error' : undefined}>
                  <option value="" disabled>Select one</option>{reasons.map((reason) => <option key={reason} value={reason}>{reason}</option>)}
                </select>{fieldError('reason')}
              </label>
              <label htmlFor="contact-message">Message
                <textarea id="contact-message" name="message" rows={7} minLength={10} maxLength={2500} required value={values.message} onChange={(event) => update('message', event.target.value)} aria-invalid={Boolean(errors.message)} aria-describedby={`message-counter${errors.message ? ' message-error' : ''}`} />
                <span className="message-meta"><span>{fieldError('message')}</span><span id="message-counter">{values.message.length} / 2500</span></span>
              </label>
              <div className="honeypot" aria-hidden="true"><label htmlFor="contact-website">Website<input id="contact-website" name="website" tabIndex={-1} autoComplete="off" value={values.website} onChange={(event) => update('website', event.target.value)} /></label></div>
              <div id="recaptcha-group" className="recaptcha-group" tabIndex={-1} aria-describedby={errors.recaptchaToken ? 'recaptchaToken-error' : undefined}>
                <RecaptchaCheckbox siteKey={siteKey} resetSignal={resetSignal} onVerify={(token) => { setRecaptchaToken(token); setErrors((current) => ({ ...current, recaptchaToken: undefined })) }} />{fieldError('recaptchaToken')}
              </div>
              <button className="button button--gold button--wide" type="submit" disabled={sending}><Send size={17} /> {sending ? 'Sending...' : 'Send request'}</button>
              <p className="form-status" role="status" aria-live="polite">{status}</p>
            </form>
          </Reveal>
        </div>
      </section>
    </>
  )
}
